/**
 * fixtures-dropdown.js
 * ------------------------------------------------------------
 * Purpose
 * - Custom UI for fixture dropdown (multiline options).
 * - Syncs selected fixture into:
 *    1) Line item property:  properties[Fixture]
 *    2) Order attribute:     attributes[Fixture]
 *
 * Requirements (fixtures-dropdown.liquid)
 * - <select> must have:
 *    - data-fixtures-select
 *    - data-form-id="BuyButtons-ProductForm-{{ section.id }}"   <-- IMPORTANT
 * Optional:
 *    - data-section-id="{{ section.id }}" (kept for backward compatibility)
 */

(function () {
  "use strict";

  function qsa(root, selector) {
    return Array.prototype.slice.call((root || document).querySelectorAll(selector));
  }

  function createHiddenInput(form, name) {
    var input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    form.appendChild(input);
    return input;
  }

  function initSelect(select) {
    if (!select) return;
    if (select.dataset.fixturesBound === "true") return;
    select.dataset.fixturesBound = "true";

    var sectionId = select.getAttribute("data-section-id") || "";
    var explicitFormId = select.getAttribute("data-form-id") || "";

    function getAddToCartForm() {
      // 1) Prefer explicit form id (most reliable)
      if (explicitFormId) {
        var explicit = document.getElementById(explicitFormId);
        if (explicit) return explicit;
      }

      // 2) Fallback to section id pattern
      var byId = sectionId ? document.getElementById("BuyButtons-ProductForm-" + sectionId) : null;
      if (byId) return byId;

      // 3) Final fallback (least reliable if multiple forms exist)
      return document.querySelector('form[data-type="add-to-cart-form"]');
    }

    function getOrCreateFixtureTargets(form) {
      if (!form) return null;

      var prop = form.querySelector('input[name="properties[Fixture]"]');
      if (!prop) prop = createHiddenInput(form, "properties[Fixture]");

      return { prop: prop};
    }

    function syncFixtureToForm() {
      var form = getAddToCartForm();
      var targets = getOrCreateFixtureTargets(form);
      if (!targets) return;

      var v = select.value || "";
      targets.prop.value = v;
    }

    // ------------------------------------------------------------
    // Custom dropdown UI
    // ------------------------------------------------------------

    var wrap = document.createElement("div");
    wrap.className = "fixture-dd";
    wrap.dataset.open = "false";

    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(select);

    select.classList.add("fixture-dd__native");

    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "fixture-dd__btn";
    btn.setAttribute("aria-haspopup", "listbox");
    btn.setAttribute("aria-expanded", "false");

    var btnLabel = document.createElement("span");
    btnLabel.className = "fixture-dd__btn-label";

    var chev = document.createElement("span");
    chev.className = "fixture-dd__chev";
    chev.textContent = "▾";

    btn.appendChild(btnLabel);
    btn.appendChild(chev);
    wrap.appendChild(btn);

    var menu = document.createElement("div");
    menu.className = "fixture-dd__menu";
    menu.setAttribute("role", "listbox");
    wrap.appendChild(menu);

    function getSelectedOption() {
      return select.options[select.selectedIndex] || null;
    }

    function setButtonText() {
      var opt = getSelectedOption();
      if (!opt) {
        btnLabel.textContent = "Select…";
        return;
      }

      if (!select.value) {
        btnLabel.textContent = (opt.textContent || "Select…").trim();
        return;
      }

      var d = opt.dataset.date || "";
      var t = opt.dataset.time || "";
      var m = opt.dataset.match || "";

      var condensed = [d, t, m].filter(Boolean).join(" · ");
      btnLabel.textContent = condensed || (opt.textContent || "").trim();
    }

    function closeMenu() {
      wrap.dataset.open = "false";
      btn.setAttribute("aria-expanded", "false");
    }

    function openMenu() {
      wrap.dataset.open = "true";
      btn.setAttribute("aria-expanded", "true");
    }

    function rebuildMenu() {
      menu.innerHTML = "";
      var selectedValue = select.value;

      Array.prototype.forEach.call(select.options, function (opt) {
        var item = document.createElement("button");
        item.type = "button";
        item.className = "fixture-dd__opt";
        item.setAttribute("role", "option");
        item.dataset.value = opt.value || "";
        item.setAttribute("aria-selected", opt.value === selectedValue ? "true" : "false");

        if (!opt.value) {
          item.textContent = (opt.textContent || "").trim();
          item.disabled = true;
          item.style.cursor = "default";
          menu.appendChild(item);
          return;
        }

        var date = opt.dataset.date || "";
        var time = opt.dataset.time || "";
        var match = opt.dataset.match || "";

        var d = document.createElement("div");
        d.className = "fixture-dd__date";
        d.textContent = date;

        var t = document.createElement("div");
        t.className = "fixture-dd__time";
        t.textContent = time;

        var m = document.createElement("div");
        m.className = "fixture-dd__match";
        m.textContent = match;

        item.appendChild(d);
        item.appendChild(t);
        item.appendChild(m);

        item.addEventListener("click", function () {
          select.value = this.dataset.value;
          select.dispatchEvent(new Event("change", { bubbles: true }));
          setButtonText();
          closeMenu();
        });

        menu.appendChild(item);
      });
    }

    btn.addEventListener("click", function () {
      rebuildMenu();
      if (wrap.dataset.open === "true") closeMenu();
      else openMenu();
    });

    document.addEventListener("click", function (e) {
      if (!wrap.contains(e.target)) closeMenu();
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape") closeMenu();
    });

    // ------------------------------------------------------------
    // Wiring
    // ------------------------------------------------------------

    select.addEventListener("change", function () {
      syncFixtureToForm();
      setButtonText();
    });

    // Ensure it’s set at submit time (edge cases)
    document.addEventListener(
      "submit",
      function (e) {
        var form = getAddToCartForm();
        if (!form || e.target !== form) return;
        syncFixtureToForm();
      },
      true
    );

    // Initial
    setButtonText();
    syncFixtureToForm();
    setTimeout(syncFixtureToForm, 50);
    setTimeout(syncFixtureToForm, 250);
  }

  function initAll() {
    qsa(document, "select[data-fixtures-select]").forEach(initSelect);
  }

  initAll();
  document.addEventListener("shopify:section:load", initAll);
})();
